import {
  forwardRef,
  useRef,
  useEffect,
  useState,
  type ChangeEvent,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon, isWeb } from "@xsolla/xui-primitives";
import {
  useModalId,
  useResolvedTheme,
  useOverlayLayer,
  LAYER_OFFSET,
} from "@xsolla/xui-core";
import { Search as SearchIcon } from "@xsolla/xui-icons-base";
import { MultiSelectControl } from "./MultiSelectControl";
import { useMultiSelect } from "./useMultiSelect";
import type { MultiSelectOption, MultiSelectProps } from "./types";
import { Portal } from "./Portal";

/** Default `menuMinWidth` when `dropdownMenu={false}`; matches `GROUP_SELECT_MIN_PANEL_WIDTH` in GroupSelect. */
const EXTERNAL_MENU_MIN_WIDTH_DEFAULT = 540;

interface MenuPosition {
  left: number;
  top: number;
  width: number;
}

export const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      errorMessage,
      value = [],
      size = "md",
      state: externalState,
      label,
      extraClear = false,
      disabled = false,
      onChange,
      variant = "tag",
      flexible = true,
      placeholder = "Select",
      removeTagsButtons = true,
      iconLeft,
      iconRight,
      maxHeight = 300,
      optionOverflow = "truncate",
      dropdownMenu = true,
      onTriggerPress,
      menuOpen = false,
      menuMinWidth,
      searchable = false,
      searchPlaceholder = "Search",
      noOptionsMessage = "No results",
      testID,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const modalId = useModalId();
    const overlayLayer = useOverlayLayer();
    const controlRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const state = externalState || (disabled ? "disable" : "default");
    const isDisable = state === "disable" || disabled;
    const isError = state === "error" || !!errorMessage;
    const isFocus = state === "focus";

    const {
      values,
      stateList,
      selectedItems,
      isOpen,
      onChoose,
      onRemove,
      onRemoveAll,
      onClose,
      onSelectClick,
    } = useMultiSelect({
      options,
      value,
      onChange,
    });

    // Close menu when disabled or built-in menu is off
    useEffect(() => {
      if (isDisable || !dropdownMenu) {
        onClose();
      }
    }, [isDisable, dropdownMenu, onClose]);

    // Focus the search input when the dropdown opens (web only)
    useEffect(() => {
      if (isOpen && searchable && isWeb) {
        searchInputRef.current?.focus();
      }
    }, [isOpen, searchable]);

    // Clear the search query whenever the dropdown closes
    useEffect(() => {
      if (!isOpen) setSearchValue("");
    }, [isOpen]);

    // Resolve the option's searchable text (labels may be non-string ReactNodes)
    const getOptionText = (opt: MultiSelectOption): string =>
      typeof opt.label === "string" ? opt.label : String(opt.value);

    // Filter options in memory when searching
    const filteredOptions =
      searchable && searchValue
        ? options.filter((opt) =>
            getOptionText(opt).toLowerCase().includes(searchValue.toLowerCase())
          )
        : options;

    const menuItems = filteredOptions.map((opt) => {
      const id = String(opt.value);
      const checked = values.map(String).includes(id);
      return {
        id,
        children: opt.label,
        checked,
        disabled: opt.disabled,
      };
    });

    const handleItemToggle = (id: string, checked: boolean) => {
      const value = options.find((opt) => String(opt.value) === id)?.value;
      if (value === undefined) return;

      const newValues = checked
        ? [...values, value]
        : values.filter((v) => v !== value);

      onChoose(newValues.map(String));
    };

    const controlMenuOpen = dropdownMenu ? isOpen : Boolean(menuOpen);
    const controlOnClick = dropdownMenu ? onSelectClick : onTriggerPress;

    // Resolve how option labels handle horizontal overflow. "scroll" mirrors
    // the legacy behavior (single line + horizontal scroll); it is web only,
    // since a native Text row cannot scroll horizontally and instead wraps.
    const isScrollOverflow = optionOverflow === "scroll";
    const isWrapOverflow = optionOverflow === "wrap";
    const optionListOverflowX = isScrollOverflow ? "auto" : "hidden";
    // Let the flex row shrink so the label can truncate/wrap within it; for
    // "scroll" the row must size to its content so the list can overflow.
    const optionRowMinWidth = isScrollOverflow ? undefined : 0;
    const optionLabelNumberOfLines =
      optionOverflow === "truncate" ? 1 : undefined;
    const optionLabelStyle = isScrollOverflow
      ? { whiteSpace: "nowrap" }
      : isWrapOverflow
        ? {
            minWidth: 0,
            whiteSpace: "normal",
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }
        : {
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          };

    useEffect(() => {
      if (!isWeb || !controlMenuOpen || isDisable || !dropdownMenu) return;

      let rafId: number | null = null;

      const repositionMenu = () => {
        rafId = null;
        if (!controlRef.current) return;

        const rect = controlRef.current.getBoundingClientRect();
        setMenuPosition({
          left: rect.left,
          top: rect.bottom + 4,
          width: rect.width,
        });
      };

      const scheduleUpdate = () => {
        if (rafId != null) return;
        rafId = requestAnimationFrame(repositionMenu);
      };

      scheduleUpdate();
      window.addEventListener("resize", scheduleUpdate);
      window.addEventListener("scroll", scheduleUpdate, true);

      return () => {
        if (rafId != null) cancelAnimationFrame(rafId);
        window.removeEventListener("resize", scheduleUpdate);
        window.removeEventListener("scroll", scheduleUpdate, true);
      };
    }, [controlMenuOpen, dropdownMenu, isDisable]);

    // Close the menu on a pointer-down outside the control or menu (web only).
    // A document-level listener is z-index agnostic, so the dropdown still
    // closes when MultiSelect is rendered inside a Modal. The menu is rendered
    // outside containerRef, so it is checked separately via composedPath
    // (falling back to node containment). Mirrors Select's click-outside close.
    useEffect(() => {
      if (!isWeb || !dropdownMenu || !isOpen || isDisable) return;

      const handleOutsideMouseDown = (event: MouseEvent) => {
        const path =
          typeof event.composedPath === "function" ? event.composedPath() : [];
        const targetNode = event.target as Node | null;
        const isInside = (el: HTMLElement | null) =>
          !!el &&
          (path.length
            ? path.includes(el)
            : !!targetNode && el.contains(targetNode));

        if (isInside(containerRef.current) || isInside(menuRef.current)) return;
        onClose();
      };

      document.addEventListener("mousedown", handleOutsideMouseDown);
      return () =>
        document.removeEventListener("mousedown", handleOutsideMouseDown);
    }, [dropdownMenu, isOpen, isDisable, onClose]);

    const externalFieldLayout = !dropdownMenu
      ? {
          width: "100%" as const,
          minWidth: menuMinWidth ?? EXTERNAL_MENU_MIN_WIDTH_DEFAULT,
          boxSizing: "border-box" as const,
        }
      : undefined;

    const dropdown = dropdownMenu &&
      isOpen &&
      !isDisable &&
      (!isWeb || menuPosition) && (
        <>
          {/* Backdrop overlay for closing (native only). On web, an outside
              mousedown listener handles closing in a z-index-agnostic way so the
              menu still closes when MultiSelect is inside a Modal. */}
          {!isWeb && (
            <Box
              data-modal-id={modalId}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: overlayLayer + LAYER_OFFSET.menu,
                cursor: "default",
              }}
              onPress={onClose}
            />
          )}

          {/* Menu */}
          <Box
            ref={menuRef}
            role="listbox"
            aria-multiselectable
            aria-label={label ?? placeholder}
            data-modal-id={modalId}
            backgroundColor={theme.colors.background.secondary}
            borderColor={theme.colors.border.secondary}
            borderWidth={1}
            borderRadius={sizeStyles.radius}
            style={{
              position: isWeb ? "fixed" : "absolute",
              top: isWeb ? menuPosition?.top : "100%",
              left: isWeb ? menuPosition?.left : 0,
              right: isWeb ? undefined : 0,
              width: isWeb ? menuPosition?.width : undefined,
              marginTop: isWeb ? undefined : 4,
              zIndex: overlayLayer + LAYER_OFFSET.popover,
              boxShadow: theme.shadow.popover,
            }}
          >
            {/* Search input - outside the scrollable area (web only) */}
            {searchable && isWeb && (
              <Box
                paddingHorizontal={sizeStyles.paddingHorizontal}
                paddingVertical={sizeStyles.paddingVertical}
                borderBottomWidth={1}
                borderColor={theme.colors.border.secondary}
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap={sizeStyles.paddingHorizontal / 2}
                  paddingHorizontal={4}
                >
                  <Icon
                    size={sizeStyles.iconSize - 2}
                    color={inputColors.placeholder}
                  >
                    <SearchIcon />
                  </Icon>
                  <Box
                    as="input"
                    ref={searchInputRef}
                    testID={testID ? `${testID}-search-input` : undefined}
                    flex={1}
                    type="text"
                    aria-label={searchPlaceholder}
                    value={searchValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSearchValue(e.target.value)
                    }
                    placeholder={searchPlaceholder}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      color: inputColors.text,
                      fontSize: sizeStyles.fontSize,
                      width: "100%",
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Options list - scrollable */}
            <Box
              paddingVertical={4}
              style={{
                maxHeight: searchable ? maxHeight - 60 : maxHeight,
                overflowY: "auto",
                overflowX: optionListOverflowX,
                scrollbarWidth: "none",
              }}
            >
              {menuItems.length === 0 ? (
                <Box
                  paddingVertical={sizeStyles.paddingVertical * 2}
                  paddingHorizontal={sizeStyles.paddingHorizontal}
                  alignItems="center"
                >
                  <Text
                    color={theme.colors.content.tertiary}
                    fontSize={sizeStyles.fontSize}
                  >
                    {noOptionsMessage}
                  </Text>
                </Box>
              ) : (
                menuItems.map((item, _index) => {
                  const brandColors = theme.colors.control.brand.primary;
                  const contentColors = theme.colors.content;

                  return (
                    <Box
                      testID={testID}
                      key={item.id}
                      paddingHorizontal={sizeStyles.paddingHorizontal}
                      paddingVertical={8}
                      onPress={() => {
                        if (!item.disabled) {
                          handleItemToggle(item.id, !item.checked);
                        }
                      }}
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                      backgroundColor={
                        item.checked ? brandColors.bg : "transparent"
                      }
                      hoverStyle={
                        !item.disabled && !item.checked
                          ? {
                              backgroundColor:
                                theme.colors.control.input.bgHover,
                            }
                          : undefined
                      }
                      style={{
                        cursor: item.disabled ? "not-allowed" : "pointer",
                        opacity: item.disabled ? 0.5 : 1,
                        minWidth: optionRowMinWidth,
                      }}
                    >
                      <Text
                        color={
                          item.checked
                            ? contentColors.on.brand
                            : theme.colors.content.secondary
                        }
                        fontSize={sizeStyles.fontSize}
                        fontWeight="400"
                        numberOfLines={optionLabelNumberOfLines}
                        style={optionLabelStyle}
                      >
                        {item.children}
                      </Text>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </>
      );

    return (
      <Box
        ref={containerRef}
        testID={testID}
        flexDirection="column"
        gap={sizeStyles.fieldGap}
        style={externalFieldLayout}
      >
        {/* Label */}
        {label && (
          <Text
            color={theme.colors.content.secondary}
            fontSize={sizeStyles.fontSize - 2}
            fontWeight="500"
          >
            {label}
          </Text>
        )}

        {/* Control container */}
        <Box
          ref={ref}
          style={{
            position: "relative",
            ...(externalFieldLayout ? { width: "100%" } : {}),
          }}
        >
          <MultiSelectControl
            ref={controlRef}
            isOpen={controlMenuOpen}
            isFocus={isFocus}
            isError={isError}
            size={size}
            state={state}
            disabled={isDisable}
            onClick={controlOnClick}
            width={dropdownMenu ? undefined : "100%"}
            removeValue={onRemove}
            removeAllValues={onRemoveAll}
            stateList={stateList}
            selectedItems={selectedItems}
            variant={variant}
            flexible={flexible}
            placeholder={placeholder}
            removeTagsButtons={removeTagsButtons}
            iconLeft={iconLeft}
            iconRight={iconRight}
            extraClear={extraClear}
          />

          {/* Dropdown menu */}
          {isWeb ? <Portal>{dropdown}</Portal> : dropdown}
        </Box>

        {/* Error message */}
        {errorMessage && (
          <Text
            color={theme.colors.content.alert.primary}
            fontSize={sizeStyles.fontSize - 2}
            style={{ lineHeight: sizeStyles.lineHeight + "px" }}
          >
            {errorMessage}
          </Text>
        )}
      </Box>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
